import { PreviewCard } from '@highlight/ui'
import { useContextBarMenuOpen } from './context-bar-wrapper'
import { useCallback, useEffect, useState } from 'react'
import { atom, useSetAtom, useAtomValue } from 'jotai'
import { selectAtom } from 'jotai/utils'
import { useOnSidepanelHidden } from '@renderer/overlay/atoms'

const openPreviewsAtom = atom(0)

export const hasOpenPreviewsAtom = selectAtom(openPreviewsAtom, (count) => {
  return count > 0
})

export const useHasOpenPreviews = () => {
  return useAtomValue(hasOpenPreviewsAtom)
}

/**
 * A context pill that previews its attachment on hover, after a beat so sweeping across the bar
 * doesn't flash cards. Stays shut while the attachments menu is open or the pill is disabled, and a
 * click on either the pill or the card dismisses it.
 */
export const PreviewableContext = ({
  children,
  preview,
  disabled,
}: React.PropsWithChildren<{ preview: React.ReactElement; disabled?: boolean }>) => {
  const isContextBarMenuOpen = useContextBarMenuOpen()
  const [isOpen, setIsOpen] = useState(false)
  const setOpenPreviews = useSetAtom(openPreviewsAtom)
  const isBlocked = disabled || isContextBarMenuOpen

  useEffect(() => {
    setOpenPreviews((prev) => Math.max(isOpen ? prev + 1 : prev - 1, 0))
  }, [isOpen])

  useEffect(() => {
    if (isBlocked) {
      setIsOpen(false)
    }
  }, [isBlocked])

  const close = useCallback(() => setIsOpen(false), [])
  useOnSidepanelHidden(close)

  return (
    <PreviewCard open={isOpen} onOpenChange={(open) => setIsOpen(open && !isBlocked)}>
      <PreviewCard.Trigger
        unstyled
        delay={300}
        closeDelay={200}
        render={children as React.ReactElement}
        className="cursor-default outline-none"
        onClick={close}
      />
      <PreviewCard.Content
        showBackdrop={false}
        className="w-fit gap-0 overflow-hidden p-0"
        positionerProps={{ side: 'top', align: 'start', sideOffset: 12, alignOffset: -4 }}
      >
        <div onClick={close}>{preview}</div>
      </PreviewCard.Content>
    </PreviewCard>
  )
}
